import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { reservationService } from '../../services/reservationService';

const ReservationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser: user } = useAuth();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingVoucher, setGeneratingVoucher] = useState(false);

  useEffect(() => {
    fetchReservationDetail();
  }, [id]);

  const generateVoucher = async () => {
    if (!reservation) {
      toast.error(t('messages.reservationNotFound'));
      return;
    }

    setGeneratingVoucher(true);
    try {
      const response = await reservationService.generateVoucher(id);
      if (response?.success && response.data) {
        const { url } = response.data;
        // Backend artık tam URL döndürüyor
        window.open(url, '_blank');
        toast.success(t('messages.voucherCreated'));
      } else {
        toast.error(t('messages.voucherCreateError'));
      }
      return; // aşağıdaki eski local PDF kodunu devre dışı bırak
    } catch (error) {
      console.error('Error creating voucher:', error);
      toast.error(t('messages.voucherCreateError'));
      return;
    } finally {
      setGeneratingVoucher(false);
    }
  };


  const fetchReservationDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reservationService.getReservationById(id);

      if (response.success) {
        setReservation(response.reservation);
      } else {
        setError(t('messages.reservationNotFound'));
      }
    } catch (error) {
      console.error('Error loading reservation detail:', error);
      setError(t('messages.reservationDetailLoadError'));
      toast.error(t('messages.reservationDetailLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('transferStatus.pending');
      case 'confirmed':
        return t('transferStatus.confirmed');
      case 'vehicle_assigned':
        return t('transferStatus.vehicle_assigned');
      case 'on_the_way_to_pickup':
        return t('transferStatus.on_the_way_to_pickup');
      case 'arrived_at_pickup':
        return t('transferStatus.arrived_at_pickup');
      case 'passenger_on_board':
        return t('transferStatus.passenger_on_board');
      case 'completed':
        return t('transferStatus.completed');
      case 'cancelled':
        return t('transferStatus.cancelled');
      case 'cancelled_by_customer':
        return t('transferStatus.cancelled_by_customer');
      case 'cancelled_by_operator':
        return t('transferStatus.cancelled_by_operator');
      case 'cancelled_by_driver':
        return t('transferStatus.cancelled_by_driver');
      case 'no_show':
        return t('transferStatus.no_show');
      default:
        return status;
    }
  };

  const getPaymentStatusText = (status) => {
    switch (status) {
      case 'paid':
        return t('paymentStatus.paid');
      case 'pending':
        return t('paymentStatus.pending');
      case 'partially_paid':
        return t('paymentStatus.partially_paid');
      case 'refunded':
        return t('paymentStatus.refunded');
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <button
            onClick={() => navigate('/reservations')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            {t('actions.backToReservations')}
          </button>
        </div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-xl mb-4">{t('messages.reservationNotFound')}</div>
          <button
            onClick={() => navigate('/reservations')}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
          >
            {t('actions.backToReservations')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/reservations')}
              className="flex items-center text-indigo-600 hover:text-indigo-800 mb-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('actions.backToReservations')}
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('pages.reservationDetail')}
            </h1>
            <p className="text-gray-600">
              {reservation.reservation_number}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(reservation.status)}`}>
              {getStatusText(reservation.status)}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Ana Bilgiler */}
        <div className="lg:col-span-2 space-y-6">
          {/* Temel Bilgiler */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.basicInfo')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.reservationNumber')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.reservation_number}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.agencyReference')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.agency_reference || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.agency')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.agency?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.createdAt')}</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(reservation.created_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.from')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.fromRegion?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.to')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.toRegion?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.vehicleType')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.vehicleType?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.passengerCount')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.passenger_count || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.hotelName')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.hotel_name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.secondaryHotelName')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.secondary_hotel_name || '-'}</p>
              </div>
              <div>
                  <label className="block text-sm font-medium text-gray-500">{t('fields.roundTrip')}</label>
                  <p className="mt-1 text-sm text-gray-900">{reservation.is_round_trip ? t('common.yes') : t('common.no')}</p>
                </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.createdBy')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.creator?.full_name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('fields.updatedAt')}</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(reservation.updated_at)}</p>
              </div>
            </div>
          </div>

          {/* Ödeme Bilgileri - Taşıyıcı kullanıcıları için gizle */}
          {user?.role !== 'carrier_user' && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.paymentInfo')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">{t('fields.paymentStatus')}</label>
                  <p className="mt-1 text-sm text-gray-900">{getPaymentStatusText(reservation.payment_status)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">{t('fields.paymentMethod')}</label>
                  <p className="mt-1 text-sm text-gray-900">{reservation.payment_method || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">{t('fields.paidAmount')}</label>
                  <p className="mt-1 text-sm text-gray-900">{reservation.paid_amount} {reservation.currency}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">{t('fields.paymentNotes')}</label>
                  <p className="mt-1 text-sm text-gray-900">{reservation.payment_notes || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Yolcu Bilgileri */}
          {reservation.passengers && reservation.passengers.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.passengerInfo')}</h2>
              <div className="space-y-4">
                {reservation.passengers.map((passenger, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">{t('fields.passenger')} {index + 1}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.firstName')}</label>
                        <p className="mt-1 text-sm text-gray-900">{passenger.first_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.lastName')}</label>
                        <p className="mt-1 text-sm text-gray-900">{passenger.last_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.nationality')}</label>
                        <p className="mt-1 text-sm text-gray-900">{passenger.nationality || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.gender')}</label>
                        <p className="mt-1 text-sm text-gray-900">{passenger.gender === 'male' ? t('common.male') : t('common.female')}</p>
                      </div>
                      {passenger.email && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500">{t('fields.email')}</label>
                          <p className="mt-1 text-sm text-gray-900">{passenger.email}</p>
                        </div>
                      )}
                      {passenger.phone && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500">{t('fields.phone')}</label>
                          <p className="mt-1 text-sm text-gray-900">{passenger.phone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transfer Bilgileri */}
          {reservation.transfers && reservation.transfers.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.transferInfo')}</h2>
              <div className="space-y-4">
                {reservation.transfers.map((transfer, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-gray-900">
                        {transfer.is_return ? t('common.returnTransfer') : t('common.arrivalTransfer')}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transfer.status)}`}>
                        {getStatusText(transfer.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.date')}</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {transfer.transfer_date ? new Date(transfer.transfer_date).toLocaleDateString('tr-TR') : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.time')}</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {transfer.transfer_time ? transfer.transfer_time.substring(0, 5) : '-'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.flightNumber')}</label>
                        <p className="mt-1 text-sm text-gray-900">{transfer.flight_number || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.roomNumber')}</label>
                        <p className="mt-1 text-sm text-gray-900">{transfer.room_number || '-'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.babySeat')}</label>
                        <p className="mt-1 text-sm text-gray-900">{transfer.baby_seat_count || '0'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">{t('fields.childSeat')}</label>
                        <p className="mt-1 text-sm text-gray-900">{transfer.child_seat_count || '0'}</p>
                      </div>

                      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-500">{t('fields.commission')}</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {transfer.commission_amount} {transfer.commission_currency} ({transfer.commission_status})
                          </p>
                        </div>
                        {reservation.is_round_trip && (
                          <div>
                            <label className="block text-sm font-medium text-gray-500">{t('fields.transferPrice') || 'Transfer Fiyatı'}</label>
                            <p className="mt-1 text-sm text-gray-900">
                              {transfer.is_return ? reservation.return_price : reservation.arrival_price} {reservation.currency}
                            </p>
                          </div>
                        )}
                      </div>
                      {(transfer.collection_amount || transfer.collection_currency) && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-500">{t('fields.collection')}</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {transfer.collection_amount || '0'} {transfer.collection_currency || 'EUR'}
                          </p>
                        </div>
                      )}
                      {transfer.vehicle && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">{t('fields.vehiclePlate')}</label>
                            <p className="mt-1 text-sm text-gray-900">{transfer.vehicle.plate_number}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">{t('fields.driverName')}</label>
                            <p className="mt-1 text-sm text-gray-900">{transfer.vehicle.driver_name}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-500">{t('fields.driverPhone')}</label>
                            <p className="mt-1 text-sm text-gray-900">{transfer.vehicle.driver_phone}</p>
                          </div>
                        </>
                      )}
                      {(transfer.driver_note || transfer.operation_note) && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-500">{t('fields.notes')}</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {transfer.driver_note && (
                              <span><strong>{t('fields.driverNote')}:</strong> {transfer.driver_note}<br /></span>
                            )}
                            {transfer.operation_note && (
                              <span><strong>{t('fields.operationNote')}:</strong> {transfer.operation_note}</span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Log Kayıtları */}
          {reservation.logs && reservation.logs.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('sections.transactionHistory')}</h3>
              <div className="space-y-4">
                {reservation.logs.map((log, index) => (
                  <div key={index} className="border-l-4 border-indigo-200 pl-4 py-2">
                    <div className="flex justify-between">
                      <p className="text-sm font-medium text-gray-900">{log.user?.full_name || 'Sistem'}</p>
                      <p className="text-sm text-gray-500">{formatDate(log.action_time)}</p>
                    </div>
                    <p className="text-sm text-gray-600">{log.action_details}</p>
                    <p className="text-xs text-gray-400">IP: {log.ip_address}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Yan Panel */}
        <div className="space-y-6">
          {/* Fiyat Bilgileri - Taşıyıcı kullanıcıları için gizle */}
          {user?.role !== 'carrier_user' && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.priceInfo')}</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('fields.totalPrice')}</span>
                  <span className="text-lg font-medium text-gray-900">
                    {reservation.price} {reservation.currency}
                  </span>
                </div>
                {reservation.is_round_trip && (
                  <>
                    <div className="flex justify-between text-xs text-gray-500 pl-4 border-l-2 border-gray-100">
                      <span>{t('fields.arrivalPrice') || 'Gidiş Fiyatı'}</span>
                      <span>{reservation.arrival_price} {reservation.currency}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 pl-4 border-l-2 border-gray-100">
                      <span>{t('fields.returnPrice') || 'Dönüş Fiyatı'}</span>
                      <span>{reservation.return_price} {reservation.currency}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('fields.paidAmount')}</span>
                  <span className="text-sm text-gray-900">
                    {reservation.paid_amount} {reservation.currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">{t('fields.remainingAmount')}</span>
                  <span className="text-sm text-gray-900">
                    {(parseFloat(reservation.price) - parseFloat(reservation.paid_amount)).toFixed(2)} {reservation.currency}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* İletişim Bilgileri */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.contactInfo')}</h2>
            <div className="space-y-3">
              {user?.role !== 'carrier_user' && (
              <div>
                <label className="block text-sm font-medium text-gray-500">{t('sections.agencyInfo')}</label>
                <p className="mt-1 text-sm text-gray-900">{reservation.agency?.name}</p>
                <p className="text-sm text-gray-900">{reservation.agency?.email}</p>
                <p className="text-sm text-gray-900">{reservation.agency?.phone}</p>
                <p className="text-sm text-gray-900">{reservation.agency?.contact_person}</p>
              </div>
              )}
              {reservation.contactDetails && (
                <>
                  {reservation.contactDetails.email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">{t('fields.email')}</label>
                      <p className="mt-1 text-sm text-gray-900">{reservation.contactDetails.email}</p>
                    </div>
                  )}
                  {reservation.contactDetails.phone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">{t('fields.phone')}</label>
                      <p className="mt-1 text-sm text-gray-900">{reservation.contactDetails.phone}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* İşlemler - Taşıyıcı kullanıcıları için düzenle ve voucher butonlarını gizle */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('sections.actions')}</h2>
            <div className="space-y-3">
              {user?.role !== 'carrier_user' && (
                <>
                  <button
                    onClick={() => navigate(`/reservations/${id}/edit`)}
                    className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    {t('actions.edit')}
                  </button>
                  <button
                    onClick={generateVoucher}
                    disabled={generatingVoucher}
                    className={`w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors ${generatingVoucher ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {generatingVoucher ? t('actions.creating') : t('actions.createVoucher')}
                  </button>
                </>
              )}
              {user?.role === 'carrier_user' && (
                <p className="text-sm text-gray-500 text-center py-4">
                  {t('messages.noPermission')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ReservationDetail;